import { Copy, Plug, Send, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Collapsible, Fold } from '../ui/Collapsible';
import { useParent } from './ParentContext';

interface ScopeInfo {
  name: string;
  description: string;
}
interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number | null;
}
interface Webhook {
  id: string;
  url: string;
  events: string[];
  disabled: boolean;
  createdAt: number;
  lastStatus: number | null;
  lastAt: number | null;
}

const when = (ts: number | null) => (ts ? new Date(ts).toLocaleDateString() : 'never');

/** Settings card: personal API keys and outbound webhooks, so other systems can integrate. */
export function IntegrationsSettings() {
  const { parent } = useParent();
  const disabled = parent.apiEnabled === false;
  // API keys and webhooks are a plan feature (#99).
  if (parent.plan && !parent.plan.features.includes('api')) {
    return (
      <Collapsible id="integrations" title="API & integrations" icon={<Plug size={18} />}>
        <p className="muted small">Personal API keys and webhooks aren’t included in your plan. Send us a note from Feedback to add them.</p>
      </Collapsible>
    );
  }
  return (
    <Collapsible id="integrations" title="API & integrations" icon={<Plug size={18} />}>
      <p className="muted small">
        Let another system read your ClassGuardian data or get notified of events. API keys and webhooks only ever cover the
        children you can see. See the <a href="https://github.com/jdvieira/ClassGuardian/blob/main/docs/api.md" target="_blank" rel="noreferrer">API guide</a>.
      </p>
      {disabled && (
        <p className="callout">API access has been turned off for your family by the administrator. Existing keys won’t work and webhooks are paused.</p>
      )}
      <ApiKeys />
      <Webhooks />
    </Collapsible>
  );
}

function CopyField({ value, label }: { value: string; label: string }) {
  const { toast } = useParent();
  return (
    <div className="row-between code-field">
      <code className="grow">{value}</code>
      <button
        className="btn small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            toast({ text: `${label} copied.`, tone: 'good' });
          } catch {
            toast({ text: 'Couldn’t copy — select it and copy by hand.', tone: 'alert' });
          }
        }}
      >
        <Copy size={14} /> Copy
      </button>
    </div>
  );
}

function ApiKeys() {
  const { toast } = useParent();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [scopes, setScopes] = useState<ScopeInfo[]>([]);
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [created, setCreated] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get<{ keys: ApiKey[]; scopes: ScopeInfo[] }>('/api/keys')
      .then((r) => {
        setKeys(r.keys);
        setScopes(r.scopes);
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);

  async function create() {
    setBusy(true);
    try {
      const r = await api.post<ApiKey & { token: string }>('/api/keys', { name: name.trim(), scopes: [...chosen] });
      setCreated(r.token);
      setName('');
      setChosen(new Set());
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  async function remove(key: ApiKey) {
    if (!confirm(`Delete the key “${key.name}”? Anything using it will stop working.`)) return;
    try {
      await api.del(`/api/keys/${key.id}`);
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  const toggle = (scope: string) =>
    setChosen((s) => {
      const next = new Set(s);
      next.has(scope) ? next.delete(scope) : next.add(scope);
      return next;
    });

  if (!keys) return null;

  // A fold in API & integrations (#601).
  return (
    <Fold title="API keys" summary={`${keys.length} key${keys.length === 1 ? '' : 's'}`}>
      {keys.length === 0 && <p className="muted small">No keys yet. Create one to let a system read your data.</p>}
      {keys.map((k) => (
        <div key={k.id} className="row-between list-row">
          <span className="grow">
            <strong>{k.name}</strong> <code className="muted">{k.prefix}…</code>
            <span className="muted small block">
              {k.scopes.join(', ') || 'no scopes'} · created {when(k.createdAt)} · last used {when(k.lastUsedAt)}
              {k.expiresAt ? ` · expires ${when(k.expiresAt)}` : ''}
            </span>
          </span>
          <button className="btn small danger-text" onClick={() => remove(k)} title="Delete key">
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      {created && (
        <div className="callout good">
          <p className="small">
            <strong>Copy this key now</strong> — it’s shown only once. Send it as <code>Authorization: Bearer …</code> or an{' '}
            <code>X-API-Key</code> header.
          </p>
          <CopyField value={created} label="Key" />
          <button className="btn small" onClick={() => setCreated(null)}>
            Done
          </button>
        </div>
      )}

      <details className="new-form">
        <summary className="btn small">New API key</summary>
        <div className="stack">
          <label>
            Name <span className="muted small">(so you can recognise it later)</span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="e.g. Home Assistant" />
          </label>
          <fieldset className="stack">
            <legend className="muted small">What it can read</legend>
            {scopes.map((s) => (
              <label key={s.name} className="check">
                <input type="checkbox" checked={chosen.has(s.name)} onChange={() => toggle(s.name)} />
                <span>
                  <code>{s.name}</code>
                  <span className="muted small block">{s.description}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <button className="btn primary" onClick={create} disabled={busy || !name.trim() || chosen.size === 0}>
            Create key
          </button>
        </div>
      </details>
    </Fold>
  );
}

function Webhooks() {
  const { toast } = useParent();
  const [hooks, setHooks] = useState<Webhook[] | null>(null);
  const [events, setEvents] = useState<ScopeInfo[]>([]);
  const [url, setUrl] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get<{ webhooks: Webhook[]; events: ScopeInfo[] }>('/api/webhooks')
      .then((r) => {
        setHooks(r.webhooks);
        setEvents(r.events);
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [toast]);
  useEffect(load, [load]);

  async function create() {
    setBusy(true);
    try {
      const r = await api.post<Webhook & { secret: string }>('/api/webhooks', { url: url.trim(), events: [...chosen] });
      setSecret(r.secret);
      setUrl('');
      setChosen(new Set());
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  async function act(run: () => Promise<unknown>, done?: string) {
    try {
      await run();
      if (done) toast({ text: done, tone: 'good' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  async function test(hook: Webhook) {
    try {
      const r = await api.post<{ status: number }>(`/api/webhooks/${hook.id}/test`);
      toast({ text: r.status >= 200 && r.status < 300 ? `Test delivered (HTTP ${r.status}).` : `Couldn’t deliver (${r.status < 0 ? 'no connection' : 'HTTP ' + r.status}).`, tone: r.status >= 200 && r.status < 300 ? 'good' : 'alert' });
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  const toggle = (name: string) =>
    setChosen((s) => {
      const next = new Set(s);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  if (!hooks) return null;

  // A fold in API & integrations (#601).
  const paused = hooks.filter((h) => h.disabled).length;
  return (
    <Fold title="Webhooks" summary={`${hooks.length} webhook${hooks.length === 1 ? '' : 's'}${paused ? ` · ${paused} disabled` : ''}`}>
      {hooks.length === 0 && <p className="muted small">No webhooks yet. Add a URL to get events pushed to another system.</p>}
      {hooks.map((h) => (
        <div key={h.id} className="row-between list-row">
          <span className="grow">
            <strong className="break">{h.url}</strong>
            <span className="muted small block">
              {h.events.length ? h.events.join(', ') : 'all events'} · {h.disabled ? 'disabled' : 'active'}
              {h.lastAt ? ` · last ${h.lastStatus === -1 ? 'failed' : 'HTTP ' + h.lastStatus} ${when(h.lastAt)}` : ' · never fired'}
            </span>
          </span>
          <span className="row-gap">
            <button className="btn small" onClick={() => test(h)} title="Send a test event">
              <Send size={14} />
            </button>
            <button className="btn small" onClick={() => act(() => api.patch(`/api/webhooks/${h.id}`, { disabled: !h.disabled }))}>
              {h.disabled ? 'Enable' : 'Disable'}
            </button>
            <button className="btn small danger-text" onClick={() => act(() => api.del(`/api/webhooks/${h.id}`))} title="Delete webhook">
              <Trash2 size={14} />
            </button>
          </span>
        </div>
      ))}

      {secret && (
        <div className="callout good">
          <p className="small">
            <strong>Save this signing secret now</strong> — shown only once. Each delivery carries an{' '}
            <code>X-ClassGuardian-Signature</code> header: HMAC-SHA256 of the body, keyed by this secret.
          </p>
          <CopyField value={secret} label="Secret" />
          <button className="btn small" onClick={() => setSecret(null)}>
            Done
          </button>
        </div>
      )}

      <details className="new-form">
        <summary className="btn small">New webhook</summary>
        <div className="stack">
          <label>
            URL <span className="muted small">(ClassGuardian POSTs JSON here)</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} maxLength={500} placeholder="https://example.com/hook" />
          </label>
          <fieldset className="stack">
            <legend className="muted small">Which events (none = all)</legend>
            {events.map((e) => (
              <label key={e.name} className="check">
                <input type="checkbox" checked={chosen.has(e.name)} onChange={() => toggle(e.name)} />
                <span>
                  <code>{e.name}</code>
                  <span className="muted small block">{e.description}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <button className="btn primary" onClick={create} disabled={busy || !/^https?:\/\//i.test(url.trim())}>
            Add webhook
          </button>
        </div>
      </details>
    </Fold>
  );
}
